/* ==========================================================================
   pause-motion.js
   Load in <head> of layout/theme.liquid, immediately after pause-motion.css:
       <script src="{{ 'pause-motion.js' | asset_url }}" defer="defer"></script>

   Replaces Dawn's animations.js for these sections. Do NOT put
   .scroll-trigger on anything listed in PLAN below — you would get two
   observers fighting over the same element.

   Why not Dawn's observer (brief §5.2 / §5.3):
   - Dawn staggers with setAttribute('style', …), which REPLACES the whole
     inline style attribute. Every block here carries Theme-Editor inline
     colour/spacing, so the first scroll would wipe it.
     We use el.style.setProperty('--pr-delay', …), which is additive.
   - Dawn's .scroll-trigger elements sit at opacity:0.01 in base.css and are
     only un-hidden by the observer. If the theme setting is off the script
     never loads and the element is permanently invisible. Here the hiding
     rule is gated on html.pr-motion, which only this file adds — so a
     failed script means "no animation", never "no content".
   ========================================================================== */

(function () {
  'use strict';

  /* --- config -------------------------------------------------------- */

  /* CHANGED from the delivered '0px 0px 5% 0px' (positive) after testing on
     this actual page.

     A positive value extends the trigger box BELOW the fold, and on this
     homepage that fires far too early: the hero is `aspect-ratio: 2.06`, so at
     1920px wide it is ~932px tall and the Intro Statement beneath it starts only
     ~120px below the fold. With a positive margin it fired after ~40px of scroll
     and had finished long before the visitor arrived — the section looked
     completely static. Animation nobody sees is worse than none.

     Negative means the element must be a tenth of a viewport height INSIDE the
     bottom edge before it moves. Dawn's original '-50px' did read as late, but
     that was the 800ms duration, not the trigger. At these durations a
     slightly-inside trigger plays exactly when the element is on screen. */
  var ROOT_MARGIN = '0px 0px -10% 0px';
  var STAGGER_MS = 90;
  /* Items past the cap share the last one's delay, so a long row never turns
     into a queue. 5 because the "New and Notables" row is exactly five tiles —
     at 4 the last two arrived together, which looked like a hiccup rather than
     a cap. 4 x 90ms = 360ms is still short enough to read as one gesture. */
  var STAGGER_CAP = 5;

  /* selector          → the element(s) that reveal
     type              → '' (text) | 'card' | 'image'
     stagger           → true to ladder siblings matched by the same entry
     childDelay        → ms, sets --pr-child-delay on [data-pr-child] inside

     Deliberately absent: .hero (LCP, above the fold), .fmr (interactive —
     all its motion is internal), .cc__grid, .process-steps, .faq,
     .footer-news. See MOTION-GUIDE.md §Q1 for the reasoning on each. */
  var PLAN = [
    /* 02 Intro statement — the page's one orchestrated text sequence.

       sequence, not stagger. This block is stacked vertically and sits directly
       under the hero, so observing each line separately meant the lower lines
       were already on screen and settled while the mark and heading above them
       were still waiting for their own trigger — the section filled in from the
       bottom upwards. One trigger on the container fixes the order for good.
       See tagSequence(). */
    { sel: '.intro-statement__inner', sequence: true },

    /* 04 What is Pause Ritual — image only.

       The copy used to animate too, 120ms behind the picture. That was the only
       thing separating this section from the other two image sections: the
       reveal on the photograph itself is and always was identical everywhere —
       same keyframe, same 1300ms, same 120ms delay, same curve. What made this
       one feel different was the text moving with it.

       Founder story is the reference: the picture settles, the words are simply
       there to be read the moment they are on screen. That is now what all three
       image sections do. */
    { sel: '.wipr__media', type: 'image' },

    /* 05 Collection cards — "New and Notables"
       HEADING ONLY. The row of tiles below it is deliberately still. */
    { sel: '.cc__heading', type: '' },
    /* REMOVED: the card row itself.

       MOTION-GUIDE.md row 05 is explicit — "Five tiles in a row staggering in is
       the exact effect that was rejected. Entrance stillness here also makes 06's
       ladder mean something." It was built anyway, and it caused three separate
       problems at once:

         slow      900ms each + 170ms apart = the fifth card finished 1.78s after
                   the row was triggered. On an eased scroll the visitor is past
                   the row before it has finished arriving.
         repeated  06 Insight cards ladder immediately below it. Two cascades back
                   to back is the thing that turns a considered effect into a tic —
                   which is the whole diagnosis this system was written to fix.
         restless  a row of five product shots is the calmest block on the page.
                   It should read as merchandise, not as an animation.

       The heading above it keeps its reveal, so the section still arrives. The
       cards' motion is the 620ms hover zoom in pause-hover.css — that is the one
       gesture this row was always meant to have. */

    /* 06 Insight cards — numbered, so a ladder is content-truthful */
    { sel: '.insight-cards__grid > .insight-cards__card', type: 'card', stagger: true },

    /* 07 How Your Body Resets — media only; the accordion stays still */
    { sel: '.hbr__media', type: 'image' },

    /* 09 Myth vs Ritual — card, then the ritual answer a beat later */
    { sel: '.mvr__grid > .mvr__card', type: 'card', stagger: true, childDelay: 160 },

    /* 10 Founder — portrait only. The quote must be readable on arrival. */
    { sel: '.founder__media', type: 'image' },

    /* Quiz page: "Why start a ritual?" header in order, then the three
       cards as a ladder. The icons bloom inside them (pause-arrival.css). */
    { sel: '.wsr__header', sequence: true },
    { sel: '.wsr__grid > .wsr__card', type: 'card', stagger: true }

    /* The delivered set also had an entry for a new testimonials carousel
       section. That section is not being added: testimonials live only on the
       product template (pdp-reviews.liquid, used by product.json and
       product.single.json) and the homepage has none, so the client's "presented
       like a simple list" note is about the PDP. That section is restyled in
       pdp-reviews.css instead — no new content to write. */
  ];

  /* --- guards -------------------------------------------------------- */

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var canStagger = window.matchMedia('(min-width: 750px)').matches;

  document.documentElement.classList.add('pr-motion');

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('pr-in');
      io.unobserve(entry.target);
    }
  }, { rootMargin: ROOT_MARGIN, threshold: 0 });

  function tag(el, type, delayMs, childDelayMs) {
    if (el.hasAttribute('data-pr-reveal')) return;
    el.setAttribute('data-pr-reveal', type || '');

    /* setProperty, never setAttribute('style', …) — see header note. */
    if (delayMs) el.style.setProperty('--pr-delay', delayMs + 'ms');
    if (childDelayMs) el.style.setProperty('--pr-child-delay', childDelayMs + 'ms');

    observe(el);
  }

  /*
    Sequence mode — observe the CONTAINER, ladder its children off that one
    trigger.

    Why this exists. Observing children individually is fine for a row, where
    everything crosses the fold together. For a STACKED block it is broken: each
    child gets its own trigger at its own scroll position, so the ladder never
    plays as a ladder. Worst case is a block sitting just under the fold — the
    lower lines are already on screen and settled while the heading above them is
    still waiting for its own trigger, so the section appears to fill in from the
    bottom upwards. That is the Intro Statement bug.

    One trigger on the parent fixes the whole class of problem: the order is
    fixed by DOM position, not by scroll position, so it is always top to bottom
    no matter which edge the visitor arrives from or how fast they scroll.
  */
  function tagSequence(el) {
    if (el.hasAttribute('data-pr-seq')) return;
    el.setAttribute('data-pr-seq', '');

    /* Count only children that RENDER.

       Every block-based section on this page calls {% render 'block-margins' %}
       inside its block loop, and that snippet emits a Liquid {% style %} tag — so
       a real <style> element lands in the DOM before each block. In the Intro
       Statement (3 blocks) el.children is therefore:

         [style, logo, style, heading, style, body]

       Indexing that directly gave the visible lines 90 / 270 / 450ms — every gap
       doubled, and the first line starting a beat late instead of at zero.
       MOTION-GUIDE.md row 02 specifies 90ms apart; this restores it. */
    var kids = el.children;
    var visible = 0;
    for (var i = 0; i < kids.length; i++) {
      var tag = kids[i].tagName;
      if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'LINK' || tag === 'TEMPLATE') continue;
      var step = canStagger ? visible * STAGGER_MS : 0;
      if (step) kids[i].style.setProperty('--pr-delay', step + 'ms');
      visible++;
    }

    observe(el);
  }

  function observe(el) {
    /* Already scrolled past on load (deep link, refresh mid-page): show it,
       skip the animation. */
    var rect = el.getBoundingClientRect();
    if (designMode || rect.top < 0) {
      el.classList.add('pr-in', 'pr-instant');
      return;
    }
    io.observe(el);
  }

  function apply(scope) {
    for (var p = 0; p < PLAN.length; p++) {
      var entry = PLAN[p];
      var nodes = scope.querySelectorAll(entry.sel);
      for (var n = 0; n < nodes.length; n++) {
        if (entry.sequence) {
          tagSequence(nodes[n]);
          continue;
        }
        var step = 0;
        if (entry.stagger && canStagger) {
          /* Per-entry override: the gap has to be read against the animation's
             own duration, not set once for the whole page. 90ms inside a 900ms
             card means five cards are all mid-flight together and the row reads
             as one burst; the same 90ms inside a 520ms card reads as a ladder. */
          step = Math.min(n, STAGGER_CAP - 1) * (entry.staggerMs || STAGGER_MS);
        }
        tag(nodes[n], entry.type, (entry.delay || 0) + step, entry.childDelay);
      }
    }

    /* 09: mark the ritual half as the second beat, no Liquid edit needed. */
    var ritual = scope.querySelectorAll('.mvr__group--ritual');
    for (var r = 0; r < ritual.length; r++) {
      ritual[r].setAttribute('data-pr-child', '');
    }
  }

  function boot() { apply(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme Editor re-renders sections. Re-tag the new markup, and reveal it
     immediately so merchants never stare at an invisible section. */
  document.addEventListener('shopify:section:load', function (e) {
    apply(e.target);
  });
}());
